import { ChangeEvent, createRef, FC, useCallback, useContext, useMemo, useState } from "react";
import { Cropper, ReactCropperElement } from "react-cropper";
import EditIconWhite from "../../../assets/EditIconWhite.svg";
import { AppContext } from "../../../App";
import "cropperjs/dist/cropper.css";
import "./ImageSelector.css";

interface IImageSelectorProps {
    onImageCropped: (image: File) => void;
    fileName?: string;
    cropGifs?: boolean;
}

const MAX_SIZE_IMG_MB = 5;
const ACCEPTED_FILE_TYPES = "image/png, image/jpeg, .gif";

const ImageSelector: FC<IImageSelectorProps> = ({ onImageCropped, fileName, cropGifs }) => {
    const { showToast } = useContext(AppContext);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [image, setImage] = useState<string>("");
    const cropperRef = createRef<ReactCropperElement>();

    const disableCrop = useCallback(() => {
        setImage("");
        setOpenModal(false);
    }, []);

    const dataURLToBlob = useCallback((dataURL: string): Blob => {
        const parts = dataURL.split(',');
        const byteString = atob(parts[1]);
        const mimeString = parts[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        
        return new Blob([ab], { type: mimeString });
    }, []);

    const getCropData = useCallback(async () => {
        const cropper = cropperRef.current?.cropper;
        if (!cropper) return;

        try {
            const croppedCanvas = cropper.getCroppedCanvas();
            const dataURL = croppedCanvas.toDataURL("image/png", 0.8);
            const croppedImageBlob = dataURLToBlob(dataURL);

            const croppedFile = new File(
                [croppedImageBlob], 
                fileName ?? "image.png", 
                { type: "image/png" }
            );

            await onImageCropped(croppedFile);
            disableCrop();
        } catch (error) {
            console.error("Ошибка при обрезке изображения:", error);
        }
    }, [cropperRef, dataURLToBlob, fileName, onImageCropped, disableCrop]);

    const handleFileUpload = useCallback((file: File) => {
        const fileSizeInMB = file.size / (1024 * 1024);

        if (fileSizeInMB > MAX_SIZE_IMG_MB) {
            showToast("Файл слишком большой");
            return;
        }

        if (file.type === "image/gif" && !cropGifs) {
            onImageCropped(file);
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === "string") {
                setImage(reader.result);
                setOpenModal(true);
            }
        };
        reader.readAsDataURL(file);
    }, [showToast, cropGifs, onImageCropped]);

    const uploadImage = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileUpload(file);
        }
        e.target.value = "";
    }, [handleFileUpload]);

    const modalButtons = useMemo(() => (
        <div className="image__selector--modal__window--block__button--background">
            <button
                data-testId="button-image_selector-crop"
                className="image__selector--modal__window--block__button--crop"
                onClick={getCropData}
            >
                Обрезать
            </button>
            <button
                data-testId="button-image_selector-cancel"
                className="image__selector--modal__window--block__button--close"
                onClick={disableCrop}
            >
                Отмена
            </button>
        </div>
    ), [getCropData, disableCrop]);

    const cropperComponent = useMemo(() => (
        <Cropper
            ref={cropperRef}
            style={{
                height: "600px",
                minWidth: "600px",
            }}
            zoomTo={0}
            initialAspectRatio={1}
            src={image}
            viewMode={1}
            background={false}
            responsive={true}
            autoCropArea={1}
            checkOrientation={false}
            guides={true}
        />
    ), [cropperRef, image]);

    return (
        <div>
            {openModal && (
                <div className="image__selector--modal__window">
                    <div className="image__selector--modal__window--block">
                        <div>
                            {cropperComponent}
                            <div className="image__selector--modal__window--block">
                                {modalButtons}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            <div className="image__selector--setting__elem--company__logo__button">
                <label className="image__selector--setting__elem--input-file image__selector--setting__elem--input-file__task--size">
                    <input
                        onChange={uploadImage}
                        type="file"
                        accept={ACCEPTED_FILE_TYPES}
                    />
                    <span>
                        <img src={EditIconWhite} alt="Edit icon" />
                    </span>
                </label>
            </div>
        </div>
    );
};

export default ImageSelector;